import { useDisclosure } from "@ecco/ui/use-disclosure";
import { Box } from "@ecco/ui/box";
import {
    SelectDropdown,
    SelectDropdownButton,
    SelectDropdownItem,
    SelectDropdownList,
} from "@ecco/ui/select-dropdown";
import React, { use, useRef } from "react";
import { Controller } from "./controller";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import type { Filter } from "./filterValues";
import { FILTER_DROPDOWN } from "@e2e/helpers/store-locator/constants/common";

export const FilterDropdowns = ({ filter }: { filter: Filter }) => {
    const values = filter.values;
    const filterType = filter.type;
    const menuButtonRef = useRef(undefined);
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.SortBy,
            ClickAndCollect.FilterBy,
            ClickAndCollect.distance,
            ClickAndCollect.eccoStores,
        ]);
    })();

    const { filterDistance, storeTypeFilter, setFilterDistance, setStoreTypeFilter } =
        use(Controller);
    const { onOpen, onClose } = useDisclosure();

    const filterDistanceData = {
        placeholder: microcopies[ClickAndCollect.FilterBy],
        setFilter: setFilterDistance,
        value: filterDistance?.label,
    };

    // We create the object outside the microcopy context and will be translatable
    const labelToMicrocopy = (label) => {
        return label === "ECCO Stores"
            ? microcopies[ClickAndCollect.eccoStores]
            : microcopies[ClickAndCollect.distance];
    };

    const storeTypeFilterData = {
        placeholder: `${microcopies[ClickAndCollect.SortBy]} ${labelToMicrocopy(storeTypeFilter?.label)}`,
        setFilter: setStoreTypeFilter,
        value: labelToMicrocopy(storeTypeFilter?.label),
    };

    const isDistanceFilter = filterType === "distance";

    return (
        <Box w={["100%", "auto"]} data-testid={`${FILTER_DROPDOWN}--${filterType}`}>
            <SelectDropdown
                matchWidth={true}
                variant="filled"
                onOpen={onOpen}
                onClose={onClose}
                closeOnSelect={true}
                boundary={"scrollParent"}
                flip={false}
                gutter={1}
            >
                {({ isOpen }) => (
                    <>
                        <SelectDropdownButton
                            zIndex={1}
                            ref={menuButtonRef}
                            sx={{
                                fontWeight: "bold",
                                gap: [2, 3],
                                height: 10,
                                width: ["100%", "fit-content"],
                                border: "none",
                                px: [3, 4],
                                "& span": {
                                    ml: 0,
                                    "& svg": {
                                        ml: 0,
                                    },
                                },
                            }}
                        >
                            {isDistanceFilter
                                ? filterDistanceData.value
                                    ? `${microcopies[ClickAndCollect.distance]}: ${filterDistanceData.value}`
                                    : filterDistanceData.placeholder
                                : storeTypeFilterData.placeholder}
                        </SelectDropdownButton>
                        {isOpen && (
                            <SelectDropdownList zIndex={[4, 2]}>
                                {values.map((value) => (
                                    <SelectDropdownItem
                                        py="2"
                                        px="4"
                                        fontWeight="bold"
                                        key={value.value}
                                        data-value={value.value}
                                        onClick={() => {
                                            onClose();
                                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                            isDistanceFilter
                                                ? filterDistanceData.setFilter(value)
                                                : storeTypeFilterData.setFilter(value);
                                        }}
                                    >
                                        {isDistanceFilter
                                            ? value.label
                                            : labelToMicrocopy(value.label)}
                                    </SelectDropdownItem>
                                ))}
                            </SelectDropdownList>
                        )}
                    </>
                )}
            </SelectDropdown>
        </Box>
    );
};
